import { useState, useRef, useEffect, useCallback } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api, apiUrl, ApiError, buildQueryString } from "@/lib/api";
import { Loader2, Film, Trash2, X, Star, Download, Hourglass } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";
import { useUserStore } from "@/stores/user-store";
import { hasExpiredFiles, RETENTION_DAYS } from "@/config/retention";
import type { Video } from "@/types";

const PAGE_SIZE = 20;

function useDownload() {
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async (url: string, filename: string) => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }, []);

  return { download, downloading };
}

function getFilename(video: Video) {
  const ext = video.media_type === "IMAGE" ? "png" : "mp4";
  const slug = video.prompt.slice(0, 40).replace(/[^a-zA-Z0-9]+/g, "-").replace(/-+$/, "");
  return `couchstack-${slug}.${ext}`;
}

interface LibraryExportResponse {
  success: boolean;
  download_path: string;
  included: number;
  excluded: number;
}

/** Zips every downloadable generation server-side and streams it straight to disk. */
function DownloadAllButton() {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await api.post<LibraryExportResponse>("/library/export");
      // An attachment response downloads without leaving the page.
      window.location.assign(apiUrl(res.download_path));
      // The server lists the library before the first byte; keep the spinner up meanwhile.
      setTimeout(() => setStarting(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.errorMessage : "Couldn't start the download. Please try again.");
      setStarting(false);
    }
  };

  return (
    <div className="flex items-center gap-3 shrink-0">
      {error && <span className="text-[12px] text-red-600 max-w-56 text-right">{error}</span>}
      <button
        onClick={start}
        disabled={starting}
        className="btn-glass flex items-center gap-1.5 text-[13px] py-2 px-3.5 disabled:opacity-60"
      >
        {starting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
        Download all
      </button>
    </div>
  );
}

export function AssetsPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Video | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      setDeleteTarget(null);
    },
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["videos", user?.id],
    queryFn: async ({ pageParam = 0 }) => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ user_id: user.id, limit: String(PAGE_SIZE), offset: String(pageParam) })}`,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if ((lastPage.videos?.length ?? 0) < PAGE_SIZE) return undefined;
      return allPages.reduce((sum, p) => sum + (p.videos?.length ?? 0), 0);
    },
    enabled: !!user?.id,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      if (!pages) return false;
      const allVids = pages.flatMap((p) => p.videos ?? []);
      const hasPending = allVids.some((v) => v.status === "pending" || v.status === "processing");
      return hasPending ? 5000 : false;
    },
  });

  const videos = data?.pages.flatMap((p) => p.videos ?? []) ?? [];
  // Expired runs keep their row but their files are gone, so they don't count.
  const liveVideos = videos.filter((v) => !hasExpiredFiles(v));
  const hasDownloadable = liveVideos.some((v) => v.download_url);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-8 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-[#2d3127] tracking-tight mb-1">
            My Creations
          </h1>
          <p className="text-sm text-[#8a8570]">
            {videos.length > 0
              ? `${liveVideos.length} generation${liveVideos.length === 1 ? "" : "s"}`
              : "Your creations will appear here"}
          </p>
        </div>
        {hasDownloadable && <DownloadAllButton />}
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {!isLoading && videos.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="size-16 rounded-2xl bg-white/40 border border-white/50 flex items-center justify-center mb-4">
            <Film className="size-7 text-[#b5b0a0]" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-semibold text-[#2d3127] mb-1.5">No creations yet</h3>
          <p className="text-sm text-[#8a8570] mb-6">
            Create your first piece of AI art to get started
          </p>
          <Link to="/create/text-to-image" className="btn-primary text-sm">
            Create Art
          </Link>
        </div>
      )}

      {videos.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 animate-fade-in delay-100">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} onClick={() => setSelected(video)} onDelete={() => setDeleteTarget(video.id)} />
            ))}
            {isFetchingNextPage &&
              Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={`skel-${i}`} />
              ))}
          </div>
          <div ref={sentinelRef} className="h-1" />
        </>
      )}

      {videos.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-[13px] text-[#8a8570]">
            Unhappy with a generation? <a href="mailto:support@couchstack.com" className="text-[#e8822e] font-semibold hover:underline">Send us an email</a> and we'll help you out :)
          </p>
        </div>
      )}

      {selected && (
        <AssetModal video={selected} onClose={() => setSelected(null)} />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl bg-white/50 border border-white/50 overflow-hidden">
      <div className="w-full aspect-square bg-[rgba(45,49,39,0.04)] animate-pulse" />
      <div className="px-2.5 py-2 space-y-1.5">
        <div className="h-2.5 w-3/4 rounded bg-[rgba(45,49,39,0.06)] animate-pulse" />
        <div className="flex items-center justify-between">
          <div className="h-2 w-14 rounded-full bg-[rgba(45,49,39,0.04)] animate-pulse" />
          <div className="h-2 w-8 rounded bg-[rgba(45,49,39,0.04)] animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function VideoCard({ video, onClick, onDelete }: { video: Video; onClick: () => void; onDelete: () => void }) {
  const { hasPurchased } = useUserStore();
  const { download, downloading } = useDownload();
  const displayUrl = hasPurchased ? video.url : (video.watermarked_url || video.url);
  const statusConfig: Record<string, { color: string; bg: string }> = {
    completed: { color: "text-emerald-600", bg: "bg-emerald-50" },
    processing: { color: "text-amber-600", bg: "bg-amber-50" },
    pending: { color: "text-amber-600", bg: "bg-amber-50" },
    failed: { color: "text-red-600", bg: "bg-red-50" },
  };
  const sc = statusConfig[video.status] || statusConfig.pending;
  const expired = hasExpiredFiles(video);
  const viewable = !expired && video.status === "completed";

  return (
    <div
      onClick={viewable ? onClick : undefined}
      className={cn(
        "group rounded-xl bg-white/70 backdrop-blur-sm border border-white/50 overflow-hidden hover:border-white/70 transition-all duration-200",
        viewable && "cursor-pointer hover-lift",
      )}
    >
      {viewable && displayUrl ? (
        <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden">
          {video.media_type === "IMAGE" ? (
            <img src={displayUrl} alt="Creation" className="w-full h-full object-contain" />
          ) : (
            <video
              src={`${displayUrl}#t=0.1`}
              className="w-full h-full object-contain"
              muted
              loop
              playsInline
              preload="metadata"
              onPointerEnter={(e) => e.currentTarget.play().catch(() => {})}
              onPointerLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            />
          )}
          <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); if (video.download_url) download(video.download_url, getFilename(video)); }}
              disabled={downloading}
              className="p-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/70 hover:text-white border border-white/20 hover:bg-black/60 transition-all"
            >
              {downloading ? <Loader2 className="size-3 animate-spin" /> : <Download className="size-3" />}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/70 hover:text-red-400 border border-white/20 hover:border-red-500/20 hover:bg-red-500/10 transition-all"
            >
              <Trash2 className="size-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative w-full aspect-square bg-[#f0ebe0] flex items-center justify-center">
          {expired ? (
            // Past the retention window: the files are deleted, so request nothing.
            <div className="flex flex-col items-center gap-1.5 text-center px-3">
              <Hourglass className="size-5 text-[#b5b0a0]" strokeWidth={1.5} />
              <span className="text-[11px] font-semibold text-[#8a8570]">Expired</span>
              <span className="text-[10px] text-[#b5b0a0]">Deleted after {RETENTION_DAYS} days</span>
            </div>
          ) : video.status === "failed" ? (
            <span className="text-[10px] text-red-400/60">Failed</span>
          ) : (
            <Loader2 className="size-5 animate-spin text-[#e8822e]/40" />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className={cn(
              "absolute top-2 right-2 p-1.5 rounded-lg bg-black/40 backdrop-blur-sm text-white/70 hover:text-red-400 border border-white/20 hover:border-red-500/20 hover:bg-red-500/10 transition-all",
              // Expired tiles are left in the grid for good, so keep delete reachable on touch screens.
              expired ? "md:opacity-0 md:group-hover:opacity-100" : "opacity-0 group-hover:opacity-100",
            )}
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      )}
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between">
          {expired ? (
            <span className="text-[9px] py-0.5 text-[#b5b0a0] tabular-nums">{new Date(video.created_at).toLocaleDateString()}</span>
          ) : (
            <span className={cn("text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full", sc.color, sc.bg)}>
              {video.status}
            </span>
          )}
          <span className="text-[9px] text-[#b5b0a0] tabular-nums">{video.credits_used} ★</span>
        </div>
      </div>
    </div>
  );
}

function AssetModal({ video, onClose }: { video: Video; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { hasPurchased } = useUserStore();
  const { download, downloading } = useDownload();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const displayUrl = hasPurchased ? video.url : (video.watermarked_url || video.url);
  const downloadUrl = video.download_url;

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/videos/${video.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      onClose();
    },
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} style={{ zIndex: 0 }} />

      <div
        className="relative w-full max-w-2xl mx-4 rounded-2xl overflow-hidden border border-white/40 bg-white/95 backdrop-blur-xl shadow-2xl shadow-black/20 animate-modal-in"
        style={{ zIndex: 1 }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-black/40 backdrop-blur-sm text-white/80 hover:text-white border border-white/20 transition-colors"
        >
          <X className="size-4" />
        </button>

        {displayUrl && (
          <div className="w-full bg-[#f0ebe0] flex items-center justify-center" style={{ height: "50vh" }}>
            {video.media_type === "IMAGE" ? (
              <img src={displayUrl} alt="Creation" className="max-w-full max-h-full object-contain" />
            ) : (
              <video
                src={displayUrl}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay
                loop
                playsInline
              />
            )}
          </div>
        )}

        <div className="p-4">
          <div className="flex items-center gap-3 text-[10px] text-[#8a8570] mb-4">
            <span>{video.credits_used} ★</span>
            <span>{video.media_type === "IMAGE" ? "Image" : "Video"}</span>
            <span>{new Date(video.created_at).toLocaleDateString()}</span>
          </div>

          <div className="flex items-center gap-2">
            {downloadUrl && (
              <button
                onClick={() => download(downloadUrl, getFilename(video))}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/60 border border-white/50 text-[12px] font-semibold text-[#2d3127] hover:bg-white/80 transition-all duration-200"
              >
                {downloading ? <Loader2 className="size-3 animate-spin" /> : <Download className="size-3" />}
                {hasPurchased ? `Download ${video.media_type === "IMAGE" ? "Image" : "Video"}` : "Download Preview"}
              </button>
            )}
            {confirmDelete ? (
              <>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-[12px] font-medium hover:bg-red-100 transition-all duration-200"
                >
                  {deleteMutation.isPending ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
                  Confirm Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 rounded-lg text-[12px] text-[#8a8570] hover:text-[#2d3127] transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/60 border border-white/50 text-[#8a8570] text-[12px] hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all duration-200"
              >
                <Trash2 className="size-3" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DeleteConfirmModal({
  isPending,
  onConfirm,
  onCancel,
}: {
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={onCancel} style={{ zIndex: 0 }} />
      <div
        className="relative w-full max-w-sm mx-4 rounded-2xl border border-white/40 bg-white/95 backdrop-blur-xl p-6 shadow-2xl shadow-black/20 animate-modal-in text-center"
        style={{ zIndex: 1 }}
      >
        <div className="size-12 rounded-xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="size-5 text-red-500" />
        </div>
        <h3 className="text-[15px] font-bold text-[#2d3127] mb-1">Delete Creation</h3>
        <p className="text-xs text-[#8a8570] mb-5">This action cannot be undone.</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="btn-glass flex-1 py-2.5 text-[13px]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="flex-1 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-[13px] font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
          >
            {isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
